import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { socialLogin } from '../services/api';

/**
 * Handles the OAuth redirect callback for all social providers.
 * Registered redirect URI: /auth/social/callback
 */
function SocialCallback({ onLoginSuccess }) {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const stateParam = params.get('state');
    const error = params.get('error');

    console.log('[SocialCallback] URL params:', { code: code ? code.slice(0,10)+'...' : null, stateParam, error });

    if (error) {
      const desc = params.get('error_description') || error;
      navigate('/login', { state: { error: `Sign-in was denied: ${desc}` } });
      return;
    }

    if (!code || !stateParam) {
      console.warn('[SocialCallback] Missing code or state');
      navigate('/login', { state: { error: 'Invalid callback — missing code or state.' } });
      return;
    }

    const storedState = sessionStorage.getItem('social_oauth_state');
    const provider = sessionStorage.getItem('social_oauth_provider');
    console.log('[SocialCallback] sessionStorage:', { storedState, provider });

    if (!storedState) {
      console.warn('[SocialCallback] storedState is empty — skipping (Strict Mode second mount or expired)');
      return;
    }

    sessionStorage.removeItem('social_oauth_state');
    sessionStorage.removeItem('social_oauth_provider');

    if (stateParam !== storedState) {
      console.warn('[SocialCallback] State mismatch', { stateParam, storedState });
      navigate('/login', { state: { error: 'State mismatch — please try signing in again.' } });
      return;
    }

    if (!provider) {
      console.warn('[SocialCallback] No provider in sessionStorage');
      navigate('/login', { state: { error: 'Unknown provider — please try again.' } });
      return;
    }

    const redirectUri = `${window.location.origin}/auth/social/callback`;
    console.log('[SocialCallback] Calling socialLogin:', { provider, redirectUri });

    socialLogin(provider, code, redirectUri)
      .then((response) => {
        console.log('[SocialCallback] Login success:', response.user);
        if (onLoginSuccess) onLoginSuccess(response.user);
      })
      .catch((err) => {
        console.error('[SocialCallback] Login failed:', err);
        navigate('/login', { state: { error: err.message || 'Sign-in failed. Please try again.' } });
      });
  }, [navigate, onLoginSuccess]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.spinner} />
        <p style={styles.text}>Completing sign-in…</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e0e0e0',
    borderTop: '3px solid #667eea',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 16px'
  },
  text: {
    margin: 0,
    color: '#666',
    fontSize: '15px'
  }
};

// Inject spin animation once
if (!document.querySelector('style[data-social-callback]')) {
  const s = document.createElement('style');
  s.setAttribute('data-social-callback', 'true');
  s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

export default SocialCallback;
